import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnInit,
  ChangeDetectionStrategy,
  inject,
  effect,
  WritableSignal,
  signal,
  computed,
  HostListener,
} from '@angular/core';
import { AppSidebarComponent } from './app.sidebar.component';
import { ColorScheme, LayoutService } from './service/app.layout.service';
import { MessageService, PrimeTemplate } from 'primeng/api';
import { NotificationsService } from '../../services/shared/notifications.service';
import { filter, Subject, Subscription } from 'rxjs';
import { MemberService } from '../../services/member/member.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Select } from 'primeng/select';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth/auth.service';
import { ButtonDirective } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

import { StyleClassModule } from 'primeng/styleclass';
import { Ripple } from 'primeng/ripple';
import { ToastModule } from 'primeng/toast';
import { DrawerModule } from 'primeng/drawer';
import { MessageModule } from 'primeng/message';
import { NotificationI, SignalRService } from '../../services/signalr/signal-r.service';

@Component({
    selector: 'app-topbar',
    templateUrl: './app.topbar.component.html',
    styleUrl: './app.topbar.component.scss',
    providers: [MessageService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AppSidebarComponent,
        ButtonDirective,
        TooltipModule,
        StyleClassModule,
        Ripple,
        ToastModule,
        PrimeTemplate,
        DrawerModule,
        MessageModule,
        TranslateModule,
    ]
})
export class AppTopbarComponent implements AfterViewInit, OnInit {
  layoutService = inject(LayoutService);
  el = inject(ElementRef);
  private messageService = inject(MessageService);
  private notificationService = inject(NotificationsService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);
  private signalRService = inject(SignalRService);
  router = inject(Router);

  @ViewChild('menubutton') menuButton!: ElementRef;
  @ViewChild(AppSidebarComponent) appSidebar!: AppSidebarComponent;
  @ViewChild('notification', { static: true }) notificationElement!: ElementRef;
  @ViewChild('dropdown') dropdown!: Select;
  activeItem!: number;
  private subscriptions: Subscription[] = [];
  notificationCount: number = 0;
  sidebarVisible = false;
  newMessages: any[] = [];
  notifications = computed(() => this.signalRService.notifications());
  newNotifications: PrimengNotifications[] = [];
  messageCount: number = 0;

  selectedLanguage: string;
  selectedLanguageIcon: string = '';
  dropdownVisible: boolean = false;
  profileMenuVisible: boolean = false;

  languages = [
    { label: 'Ελληνικά', value: 'gr', icon: '../../assets/i18n/icons/gr.png' },
    { label: 'English', value: 'en', icon: '../../assets/i18n/icons/en.png' },
  ];
  appName: any;

  themeMode = 'pi pi-moon';
  constructor() {
    const defaultLanguage = 'gr';
    this.translate.setDefaultLang(defaultLanguage);
    this.translate.use(defaultLanguage);
    this.selectedLanguage = defaultLanguage;
    this.updateSelectedLanguageIcon();
    document.documentElement.lang = 'el';

    effect(() => this.processNewNotifications(this.notifications()));
    // effect(() => console.log(this.notifications()));
  }

  ngOnInit() {}

  ngAfterViewInit(): void {
    this.processNewNotifications(this.notifications());
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.dropdownVisible && !this.el.nativeElement.contains(event.target)) {
      this.dropdownVisible = false;
    }
  }

  toggleDropdown() {
    this.dropdownVisible = !this.dropdownVisible;
  }

  toggleProfileMenu() {
    this.profileMenuVisible = !this.profileMenuVisible;
  }

  switchLanguage(language: string) {
    this.translate.use(language);
    localStorage.setItem('selectedLanguage', language);
    this.selectedLanguage = language;
    this.updateSelectedLanguageIcon();
    this.dropdownVisible = false;
    document.documentElement.lang = language === 'gr' ? 'el' : language;
  }

  goToInbox() {
    this.messageCount = 0;

    this.router.navigate(['/messages']);
  }
  goToSettings() {
    this.router.navigate(['/settings']);
  }

  discardMessage(message: PrimengNotifications): void {
    this.notificationService
      .deleteNotification([message.id])
      .subscribe((res) => {
        this.newNotifications = this.newNotifications.filter(
          (msg) => msg.id !== message.id
        );

        this.updateNotificationCount();
      });
  }

  clearAllNotifications(): void {
    const notificationIds = this.newNotifications.map((item) => item.id);
    this.newNotifications = [];
    this.notificationCount = 0;
    if (notificationIds.length > 0) {
      this.notificationService
        .deleteNotification(notificationIds)
        .subscribe(() => {
          this.signalRService.notifications.set([]);
        });
    }
  }

  onSidebarClose(): void {
    this.newNotifications = this.newNotifications.map((item) => ({
      ...item,
      viewed: true,
      severity: 'info',
    }));
    this.updateNotificationCount();

    this.sidebarVisible = false;
  }

  showSidebar(): void {
    this.sidebarVisible = true;
    this.messageService.clear();
    this.newNotifications = this.newNotifications.map((x) => ({ ...x }));
  }

  logout() {
    this.notificationService.stopPollingForNotifications();
    this.authService.logout();
    this.signalRService.destroyConnection();
  }

  onMenuButtonClick() {
    this.layoutService.onMenuToggle();
  }

  onSidebarButtonClick() {
    this.layoutService.showSidebar();
  }

  onConfigButtonClick() {
    this.layoutService.showConfigSidebar();
  }

  get colorScheme(): ColorScheme {
    return this.layoutService.config().colorScheme;
  }
  set colorScheme(_val: ColorScheme) {
    this.layoutService.config.update((config) => ({
      ...config,
      colorScheme: _val,
    }));
    this.layoutService.config.update((config) => ({
      ...config,
      menuTheme: _val,
    }));
    const tobarTheme =
      this.layoutService.config().topbarTheme === 'transparent'
        ? 'transparent'
        : _val;

    this.layoutService.config.update((config) => ({
      ...config,
      topbarTheme: tobarTheme,
    }));
  }
  goToMessages() {
    window.open('/messages');
  }
  changeThemeMode() {
    if (this.colorScheme == 'light') {
      this.colorScheme = 'dark';
      this.themeMode = 'pi pi-sun';
    } else {
      this.colorScheme = 'light';
      this.themeMode = 'pi pi-moon';
    }
  }

  openGreece20() {
    window.open('https://greece20.gov.gr/', '_blank');
  }

  private processNewNotifications(notifications: NotificationI[]): void {
    const newNotifications: PrimengNotifications[] = notifications
      .map((item) => {
        const exists = this.newNotifications.some((msg) => msg.id === item.id);
        if (!exists) {
          this.messageService.addAll([
            {
              severity: 'info',
              summary: 'Ειδοποίηση',
              detail: item.message,
            },
          ]);
          return {
            id: item.id,
            severity: 'info',
            summary: '',
            detail: item.message,
            viewed: false,
          };
        }
        return null; // ✅ Return null instead of []
      })
      .filter((item): item is PrimengNotifications => item !== null); // ✅ Type-safe filter

    this.newNotifications = [...this.newNotifications, ...newNotifications];

    this.updateNotificationCount();
  }

  private updateNotificationCount(): void {
    this.notificationCount = this.newNotifications.filter(
      (item) => !item.viewed
    ).length;
  }

  private updateSelectedLanguageIcon() {
    const foundLanguage = this.languages.find(
      (lang) => lang.value === this.selectedLanguage
    );
    this.selectedLanguageIcon = foundLanguage ? foundLanguage.icon : '';
  }

  private updateMessageCount(): void {
    this.messageCount = this.newMessages.filter(
      (item: any) => item.unreadMessageCount >= 1
    ).length;
  }
  private processNewMessages(messages: any): void {
    if (!messages || !Array.isArray(messages)) {
      return;
    }

    messages.forEach((msg: any) => {
      const existingMsgIndex = this.newMessages.findIndex(
        (existingMsg: any) => existingMsg.id === msg.id
      );

      if (existingMsgIndex !== -1) {
        this.newMessages[existingMsgIndex] = msg;
      } else {
        this.newMessages.push(msg);
      }
    });

    this.updateMessageCount();
  }
}

export interface PrimengNotifications {
  id: string;
  severity: string;
  summary: string;
  detail: string;
  viewed: boolean;
}
